import React from 'react';
import { useAbastecimentos, PeriodoAbast, AbastRecord } from 'src/controllers/Dashboard/FleetAdmin/AbastecimentosController';

const fmtL = (v: number) => v.toLocaleString('pt-BR') + ' L';
const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const PERIODOS: { key: PeriodoAbast; label: string }[] = [
    { key: 'hoje', label: 'Hoje' },
    { key: 'semana', label: 'Semana' },
    { key: 'mes', label: 'Mês' },
    { key: 'ano', label: 'Ano' },
];

const categoriaIcon = (cat: string): string => {
    if (!cat) return '🚗';
    const c = cat.toLowerCase();
    if (c.includes('ônibus') || c.includes('onibus') || c.includes('micro')) return '🚌';
    if (c.includes('ambulância') || c.includes('ambulancia')) return '🚑';
    if (c.includes('caminhão') || c.includes('caminhao')) return '🚛';
    if (c.includes('moto')) return '🏍️';
    if (c.includes('van') || c.includes('kombi')) return '🚐';
    return '🚗';
};

const horaStr = (horaRaw: string): string => {
    const d = new Date(horaRaw);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const dark = '#1e1e2e';
const dark2 = '#2a2a3e';

const AbastItem: React.FC<{ record: AbastRecord }> = ({ record }) => (
    <div style={{
        display: 'flex',
        gap: 12,
        padding: '0.9rem 0',
        borderBottom: '1px solid #2a2a3e',
        alignItems: 'flex-start',
    }}>
        {/* ícone veículo */}
        <div style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: record.fraude ? '#7f1d1d' : '#1d3a5f',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            flexShrink: 0,
        }}>
            {categoriaIcon(record.categoria)}
        </div>

        {/* info */}
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{record.placa}</span>
                {record.fraude && (
                    <span
                        title={
                            record.tipoFraude === 'EXCESSO'
                                ? 'Excesso de capacidade do tanque'
                                : 'Duplo abastecimento em menos de 8h'
                        }
                        style={{
                            background: '#ef4444',
                            color: '#fff',
                            borderRadius: 6,
                            padding: '1px 7px',
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            cursor: 'help',
                        }}
                    >
                        {record.tipoFraude === 'EXCESSO' ? 'EXCESSO TANQUE' : 'DUPLO ABAST.'}
                    </span>
                )}
            </div>
            <div style={{ color: '#a0aec0', fontSize: 12, marginTop: 1 }}>
                {record.modelo}{record.departamento ? ` · ${record.departamento}` : ''}
            </div>
            <div style={{ color: '#718096', fontSize: 11, marginTop: 2 }}>
                {[record.estabelecimento, record.tipoCombustivel, horaStr(record.hora)]
                    .filter(Boolean).join(' · ')}
            </div>
            {record.tipoFraude === 'EXCESSO' && record.capacidadeTanque > 0 && (
                <div style={{ color: '#f87171', fontSize: 11, marginTop: 2 }}>
                    Capacidade cadastrada: {fmtL(record.capacidadeTanque)}
                </div>
            )}
        </div>

        {/* litros + valor */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{
                color: record.fraude ? '#f87171' : '#fff',
                fontWeight: 700,
                fontSize: 15,
            }}>
                {fmtL(record.litros)}
            </div>
            <div style={{ color: '#718096', fontSize: 11, marginTop: 2 }}>
                {fmtBRL(record.valorTotal)}
            </div>
        </div>
    </div>
);

const AbastecimentosCard: React.FC = () => {
    const { records, resumo, loading, periodo, changePeriodo } = useAbastecimentos();

    return (
        <div style={{ background: dark, borderRadius: 16, padding: '1.2rem 1.4rem', marginBottom: '1.5rem' }}>
            {/* Título */}
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 20, marginBottom: 14 }}>
                Abastecimentos
            </div>

            {/* Tabs de período */}
            <div style={{
                display: 'flex',
                background: dark2,
                borderRadius: 12,
                padding: 4,
                marginBottom: 16,
                gap: 4,
            }}>
                {PERIODOS.map(p => (
                    <button key={p.key} onClick={() => changePeriodo(p.key)} style={{
                        flex: 1,
                        padding: '6px 0',
                        borderRadius: 9,
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 13,
                        background: periodo === p.key ? '#fff' : 'transparent',
                        color: periodo === p.key ? '#1e1e2e' : '#a0aec0',
                        transition: 'all 0.2s',
                    }}>
                        {p.label}
                    </button>
                ))}
            </div>

            {/* Cards de resumo */}
            <div className="p-grid p-fluid" style={{ marginBottom: 14 }}>
                {[
                    { label: 'Total litros', value: fmtL(resumo.totalLitros), color: '#60a5fa' },
                    { label: 'Valor total', value: fmtBRL(resumo.totalValor), color: '#34d399' },
                    { label: 'Normais', value: String(resumo.normais), color: '#e2e8f0' },
                    { label: 'Fraudes', value: String(resumo.fraudes), color: resumo.fraudes > 0 ? '#f87171' : '#e2e8f0' },
                ].map((card, i) => (
                    <div key={i} className="p-col-6 p-md-3">
                        <div style={{
                            background: dark2,
                            borderRadius: 12,
                            padding: '0.7rem 0.9rem',
                            textAlign: 'center',
                        }}>
                            <div style={{ color: card.color, fontWeight: 700, fontSize: 16 }}>
                                {loading ? '—' : card.value}
                            </div>
                            <div style={{ color: '#718096', fontSize: 11, marginTop: 2 }}>
                                {card.label}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Lista */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5em', color: '#fff' }} />
                </div>
            ) : records.length === 0 ? (
                <div style={{ color: '#718096', textAlign: 'center', padding: '1rem' }}>
                    Nenhum abastecimento no período
                </div>
            ) : (
                <div>
                    {records.slice(0, 20).map((r, i) => <AbastItem key={i} record={r} />)}
                    {records.length > 20 && (
                        <div style={{ color: '#718096', fontSize: 12, textAlign: 'center', marginTop: 10 }}>
                            + {records.length - 20} abastecimentos
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AbastecimentosCard;
